"use client";

import { cn } from "@/utils";
import { Plus, Trash2 } from "lucide-react";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";

import NumberInput from "./NumberInput";

interface SubsidyItem {
  title: string;
  amount: string;
}

interface SubsidiesInputProps extends Omit<React.ComponentPropsWithRef<"div">, "onChange"> {
  value: SubsidyItem[];
  onChange: (value: SubsidyItem[]) => void;
}

const SubsidiesInput = forwardRef<HTMLDivElement, SubsidiesInputProps>(function SubsidiesInput(
  { className, value, onChange, ...props },
  ref,
) {
  const { t } = useTranslation();

  const handleAdd = () => {
    onChange([...value, { title: "", amount: "" }]);
  };

  const handleRemove = (index: number) => {
    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
  };

  const handleChange = (index: number, field: "title" | "amount", newValue: string) => {
    const current = value[index];
    if (!current) return;
    const updated = [...value];
    updated[index] = { ...current, [field]: newValue };
    onChange(updated);
  };

  const canDelete = (): boolean => {
    return value.length > 1;
  };

  return (
    <div ref={ref} className={cn("space-y-2", className)} {...props}>
      {value.map((subsidy, position) => (
        <div key={`subsidy-${position}`} className="flex gap-2 items-start">
          {/* Title Input */}
          <div className="flex-1">
            <input
              type="text"
              value={subsidy.title}
              onChange={(e) => handleChange(position, "title", e.target.value)}
              className="w-full px-2 py-1 text-xs border border-border-secondary rounded-md focus:outline-none focus:ring-1 focus:ring-ring-focus focus:border-transparent transition-colors bg-bg-primary"
              placeholder={t(
                "projectWizard.budgetInvestment.subsidies.titlePlaceholder",
                "Nombre de la ayuda pública",
              )}
            />
          </div>

          {/* Amount Input */}
          <div className="w-32 relative">
            <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-xs text-text-tertiary">
              €
            </span>
            <NumberInput
              value={subsidy.amount}
              onChange={(next) => handleChange(position, "amount", next)}
              className="w-full pl-6 pr-2 py-1 text-xs border border-border-secondary rounded-md focus:outline-none focus:ring-1 focus:ring-ring-focus focus:border-transparent transition-colors bg-bg-primary"
              placeholder="0,00"
            />
          </div>

          {/* Remove Button - Only show if we have more than 1 row */}
          {canDelete() ? (
            <button
              type="button"
              onClick={() => handleRemove(position)}
              className="p-1 rounded-md hover:bg-danger-surface transition-colors"
              title={t("projectWizard.budgetInvestment.subsidies.remove", "Eliminar")}
            >
              <Trash2 size={14} className="text-danger-hover" />
            </button>
          ) : (
            // Spacer to maintain layout when delete button is hidden
            <div className="w-6" />
          )}

          {/* Add Button - Only on last row */}
          {position === value.length - 1 && (
            <button
              type="button"
              onClick={handleAdd}
              className="p-1 rounded-md bg-primary-hover hover:bg-primary transition-colors"
              title={t("projectWizard.budgetInvestment.subsidies.add", "Añadir ayuda")}
            >
              <Plus size={14} className="text-text-inverse" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
});

export default SubsidiesInput;
